"use client";

import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, useEffect, useRef, memo } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils";
import useFramerMotion, { LazyMotion, AnimatePresence, MotionConfig, m, type Variants, type Transition } from "@/hooks/useFramerMotion";
import { useOnClickOutside } from "usehooks-ts";
import { useEscapeKey } from "@/hooks/useEscapeKey";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { FiX } from "react-icons/fi";
import { Meteors } from "./Meteors";
import { useTranslations } from "next-intl";
const MeteorsPure = memo(Meteors);
export const springProps: Transition = {
  type: "spring",
  stiffness: 260,
  damping: 15
};
const transitionProps = {
  x: springProps,
  y: springProps,
  scale: springProps,
  rotateX: springProps,
  opacity: {
    duration: 0.3
  }
};
export const variantsBackdrop = {
  opened: {
    opacity: 1
  },
  enter: {
    opacity: 1
  },
  leave: {
    opacity: 0
  }
};
export const variantsModal: Variants = {
  opened: {
    opacity: 1,
    scale: 1,
    rotateX: 0,
    y: 0,
    transition: transitionProps
  },
  enter: {
    opacity: 1,
    scale: 1,
    rotateX: 0,
    y: 0,
    transition: transitionProps
  },
  leave: {
    opacity: 0,
    scale: 0.5,
    y: "15%",
    rotateX: 5,
    transition: transitionProps
  }
};

// Prop-driven modal chrome: portal, backdrop, open/close animation, escape
// key, click-outside, body scroll lock and dialog focus — in ONE place. It
// deliberately holds no open-state of its own: state lives in module stores
// (ContactModalContext, PortfolioModalContext), so triggers can live anywhere
// and never need to wrap anything (TODO item 4).
export const ModalShell = t0 => {
  const $ = _c(27);
  const {
    open,
    onClose,
    children,
    ariaLabel,
    className,
    backdropClassName,
    wrapperClassName,
    closeBtnClassname,
    closeIconClassName: t1,
    initialFocus
  } = t0;
  const closeIconClassName = t1 === undefined ? "size-8" : t1;
  const modalRef = useRef(null);
  const framerMotionFeatures = useFramerMotion();
  const t = useTranslations("modal");
  const {
    lock,
    unlock
  } = useLockBodyScroll();
  useDialogFocus(modalRef, open, initialFocus);
  let t2;
  if ($[0] !== onClose || $[1] !== open) {
    t2 = () => {
      if (open) {
        onClose();
      }
    };
    $[0] = onClose;
    $[1] = open;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useOnClickOutside(modalRef, t2);
  let t3;
  if ($[3] !== onClose || $[4] !== open) {
    t3 = () => {
      if (open) {
        onClose();
      }
    };
    $[3] = onClose;
    $[4] = open;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEscapeKey(t3);
  let t4;
  let t5;
  if ($[6] !== lock || $[7] !== open || $[8] !== unlock) {
    t4 = () => {
      if (open) {
        lock();
      } else {
        unlock();
      }
    };
    t5 = [open, lock, unlock];
    $[6] = lock;
    $[7] = open;
    $[8] = unlock;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== ariaLabel || $[12] !== backdropClassName || $[13] !== children || $[14] !== className || $[15] !== closeBtnClassname || $[16] !== closeIconClassName || $[17] !== onClose || $[18] !== open || $[19] !== t || $[20] !== wrapperClassName) {
    t6 = open && <>{createPortal(<m.div key="backdrop" variants={variantsBackdrop} initial="leave" animate="opened" exit="leave" className={cn("fixed inset-0 size-full", backdropClassName)}><div className={wrapperClassName ?? "contents"}><m.div key="modal" variants={variantsModal} ref={modalRef} role="dialog" aria-modal="true" aria-label={ariaLabel || t("dialogLabel")} tabIndex={-1} className={cn("relative focus:outline-none", className)} initial="leave" animate="opened" exit="leave"><button title={t("close")} type="button" onClick={() => onClose()} className={cn("absolute z-10 flex items-center justify-center", closeBtnClassname)}><FiX className={closeIconClassName} /></button>{children}</m.div></div></m.div>, document.body)}</>;
    $[11] = ariaLabel;
    $[12] = backdropClassName;
    $[13] = children;
    $[14] = className;
    $[15] = closeBtnClassname;
    $[16] = closeIconClassName;
    $[17] = onClose;
    $[18] = open;
    $[19] = t;
    $[20] = wrapperClassName;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t6) {
    t7 = <MotionConfig reducedMotion="user"><AnimatePresence mode="wait">{t6}</AnimatePresence></MotionConfig>;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== framerMotionFeatures || $[25] !== t7) {
    t8 = <LazyMotion features={framerMotionFeatures}>{t7}</LazyMotion>;
    $[24] = framerMotionFeatures;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
export const ModalContent = t0 => {
  const $ = _c(6);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative flex flex-1 flex-col overflow-hidden", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MeteorsPure number={40} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t1) {
    t3 = <div className={t1}>{t2}{children}</div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};